import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismCard } from '@mylighthouse/prism-ember';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { t } from 'ember-intl';
import GeneralDatepickersPickerPeriod from 'lumen/components/general/datepickers/picker-period';
import ParityChartsChartParityEvolution from 'lumen/components/parity/charts/chart-parity-evolution';
import ParityChartsChartParityScoreGauge from 'lumen/components/parity/charts/chart-parity-score-gauge';
import type {
  BreakdownItem,
  HistoricalData,
} from 'lumen/routes/market/parity/insight';
import {
  DATE_AGGREGATION_MONTH,
  type DateAggregation,
  HISTORICAL_POINTS_AMOUNT,
  LATEST,
  type ParityFilter,
  type ParityFilterOrBenchmark,
} from 'lumen/utils/constants/pi-constant-values';

import styles from './historical-block.module.css';

interface ParityInsightHistoricalBlockSignature {
  Args: {
    dateAggregation: DateAggregation;
    extractDate: string;
    extractDateKey: string;
    hideHotelsMetric?: boolean;
    historicalChartData?: HistoricalData | null;
    historicalExtractDate: string;
    historicalExtractDateMoment: Dayjs;
    isBenchmarkVisible?: boolean;
    isLoading: boolean;
    isLoadingGaugeData: boolean;
    maxDate: string;
    parityFilters: Set<ParityFilter>;
    periodChanged: (period: Dayjs) => void;
    updateHistoricalPeriod: (period: Dayjs) => void;
    updateParityFilters: (filters: ParityFilterOrBenchmark) => void;
    widgetData?: BreakdownItem;
  };
}

export default class ParityInsightHistoricalBlock extends Component<ParityInsightHistoricalBlockSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get beginReached() {
    const previousPeriod = this.args.historicalExtractDateMoment.subtract(
      HISTORICAL_POINTS_AMOUNT,
      this.args.dateAggregation,
    );
    return previousPeriod.isBefore(
      this.hotelAndMarketService.hotelOrMarket.parityFirstExtractDate,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get endReached() {
    const nextPeriod = this.args.historicalExtractDateMoment.add(
      1,
      this.args.dateAggregation,
    );
    return nextPeriod.isAfter(
      dayjs(this.args.maxDate),
      this.args.dateAggregation,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedPeriodRange() {
    if (this.args.isLoading || !this.leastRecentDate || !this.mostRecentDate) {
      return null;
    }

    const leastRecentMoment = dayjs(this.leastRecentDate);
    const mostRecentMoment = dayjs(this.mostRecentDate);

    if (this.args.dateAggregation === DATE_AGGREGATION_MONTH) {
      return `${leastRecentMoment.format('MMM')} - ${mostRecentMoment.format('MMM')}`;
    }

    // For weekly: show "Today" for this week or the last day of the last week in the range
    let formattedMostRecentMoment = this.intl.t('general.today');
    if (this.mostRecentDate !== LATEST) {
      formattedMostRecentMoment = mostRecentMoment.add(6, 'day').format('ll');
    }

    return `${leastRecentMoment.format('ll')} - ${formattedMostRecentMoment}`;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get leastRecentDate() {
    if (
      !this.args.historicalChartData ||
      !Object.keys(this.args.historicalChartData.all).length
    ) {
      return null;
    }

    return Object.keys(this.args.historicalChartData.all)[0];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get mostRecentDate() {
    if (
      !this.args.historicalChartData ||
      !Object.keys(this.args.historicalChartData.all).length
    ) {
      return null;
    }

    return Object.keys(this.args.historicalChartData.all).at(-1);
  }

  <template>
    <div>
      <PrismCard @contentLayout="borderedHeader" @isParent={{true}}>
        <:header as |Header|>
          <Header.title>
            {{t "parity_analytics.chart_title"}}
          </Header.title>
        </:header>

        <:headerAfter>
          <div class="flex items-center justify-end gap-200">
            {{#if @isLoading}}
              <div class="loading-pulse w-[150px]" />
            {{else}}
              <p class="text-300--regular">{{this.formattedPeriodRange}}</p>
            {{/if}}

            <GeneralDatepickersPickerPeriod
              {{! @glint-expect-error: Incorrect type }}
              @beginReached={{this.beginReached}}
              @endReached={{this.endReached}}
              @historicalExtractDateMoment={{@historicalExtractDateMoment}}
              @periodChanged={{@updateHistoricalPeriod}}
              @periodType={{@dateAggregation}}
            />
          </div>
        </:headerAfter>

        <:content>
          <div class={{styles.card-content}}>
            <ParityChartsChartParityEvolution
              @customLegendItemClick={{@updateParityFilters}}
              @data={{@historicalChartData}}
              @dateAggregation={{@dateAggregation}}
              @extractDate={{@extractDate}}
              @hideHotelsMetric={{@hideHotelsMetric}}
              {{! @glint-expect-error: Incorrect type }}
              @historicalExtractDate={{@historicalExtractDate}}
              @isBenchmarkVisible={{@isBenchmarkVisible}}
              @isLoading={{@isLoading}}
              @parityFilters={{@parityFilters}}
              @periodChanged={{@periodChanged}}
            />
            <ParityChartsChartParityScoreGauge
              @data={{@widgetData}}
              @dateAggregation={{@dateAggregation}}
              @extractDate={{@extractDate}}
              @extractDateKey={{@extractDateKey}}
              @hideHotelsMetric={{@hideHotelsMetric}}
              @historicalChartData={{@historicalChartData}}
              @isBenchmarkVisible={{@isBenchmarkVisible}}
              @isLoading={{@isLoadingGaugeData}}
              @parityFilters={{@parityFilters}}
            />
          </div>
        </:content>
      </PrismCard>
    </div>
  </template>
}
